import classes from './BookedCardPhone.module.css';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFetchCreativePackageImage } from '../../hooks/creative';
import { useTextTruncate } from '../../hooks/useTextTruncate';
import { hasUnviewedStatusChange } from '../../util/bookingViewedTracker';
import { isCompletedBookingStatus } from '../../util/bookingDateTime';
import { getStudioImageSrc } from '../../util/studioImageUtils';

const BookedCardPhone = ({ studio, user, bookingId, creativePackages }) => {
    const navigate = useNavigate();
    const { user: authUser } = useSelector((state) => state?.auth);
    const userId = authUser?.id || authUser?._id;
    
    // Check if this booking has unviewed status changes (notification)
    const hasNotification = hasUnviewedStatusChange({ id: bookingId, status: studio?.status }, userId);
    
    // Get the first package ID from creativePackages
    const firstPackageId = creativePackages?.[0]?.packageID;
    
    // Fetch the first image of the package
    const { firstImage: packageImage, packageName, loading: packageImageLoading } = useFetchCreativePackageImage(firstPackageId);
    const { truncatedText: truncatedPackageName } = useTextTruncate(packageName, {
        maxChars: 70,
        multiline: true
    });

    // Helper functions for status display
    const calculateHoursRemaining = (createdAt) => {
        if (!createdAt) return null;
        const created = new Date(createdAt);
        const now = new Date();
        const hoursPassed = (now - created) / (1000 * 60 * 60);
        const hoursRemaining = Math.max(0, Math.ceil(24 - hoursPassed));
        return hoursRemaining;
    };

    const isBookingExpired = (createdAt) => {
        if (!createdAt) return false;
        const created = new Date(createdAt);
        const now = new Date();
        const hoursPassed = (now - created) / (1000 * 60 * 60);
        return hoursPassed > 24;
    };

    const getStatusDisplay = () => {
        const status = studio?.status;
        const hostName = studio?.hostName || '';
        const cancelledBy = studio?.cancelledBy;
        const createdAt = studio?.createdAt;
        const hoursRemaining = calculateHoursRemaining(createdAt);
        const expired = isBookingExpired(createdAt);
        const completed = isCompletedBookingStatus(status);

        // Check if cancelled by guest or host
        const cancelledByGuest = cancelledBy === "GUEST";
        const cancelledByHost = cancelledBy === "HOST";
        const cancelledBySystem = cancelledBy === "SYSTEM";

        if (completed) {
            return {
                message: '',
                statusText: 'Abgeschlossen',
                color: '#A1A7AE'
            };
        }

        if (status === "NO_SHOW") {
            return {
                message: 'Du bist nicht erschienen',
                statusText: 'Nicht erschienen',
                color: '#CE425D'
            };
        }

        if (status === "EXPIRED" || (expired && status === "PENDING")) {
            return {
                message: 'Die Anfrage ist abgelaufen',
                statusText: 'Anfrage abgelaufen',
                color: '#CE425D'
            };
        }

        if (status === "INQUIRY" || (status === "PENDING" && !expired && hoursRemaining === null)) {
            return {
                message: `Du hast dich bei ${hostName} erkundigt`,
                statusText: 'Erkundigung',
                color: '#2788EE'
            };
        }

        if (status === "PENDING" && !expired) {
            return {
                message: `Du hast bei ${hostName} angefragt`,
                statusText: `Angefragt: Läuft in ${hoursRemaining} ${hoursRemaining === 1 ? 'Stunde' : 'Stunden'} ab`,
                color: '#F7871E'
            };
        }

        if (status === "WITHDRAWN") {
            return {
                message: `Du hast die Anfrage zurückgezogen`,
                statusText: 'Anfrage zurückgezogen',
                color: '#CE425D'
            };
        }

        if (status === "DECLINED" || status === "REJECTED") {
            return {
                message: `${hostName} hat die Buchungsanfrage abgelehnt`,
                statusText: 'Anfrage abgelehnt',
                color: '#CE425D'
            };
        }

        if (status === "CONFIRMED") {
            return {
                message: `${hostName} hat die Buchungsanfrage bestätigt`,
                statusText: 'Buchung bestätigt',
                color: '#20BD13'
            };
        }

        if (status === "CANCELLED") {
            // Entdecker mode
            if (cancelledByHost) {
                return {
                    message: `${hostName} hat die Buchung storniert`,
                    statusText: `Von ${hostName} storniert`,
                    color: '#CE425D'
                };
            } else if (cancelledByGuest) {
                return {
                    message: `Du hast die Buchung storniert`,
                    statusText: 'Von dir storniert',
                    color: '#CE425D'
                };
            } else if (cancelledBySystem) {
                return {
                    message: 'Die Buchung wurde automatisch storniert',
                    statusText: 'Storniert',
                    color: '#CE425D'
                };
            } else {
                // Fallback if cancelledBy is not available - assume host cancelled
                return {
                    message: `${hostName} hat die Buchung storniert`,
                    statusText: 'Storniert',
                    color: '#CE425D'
                };
            }
        }

        // Default fallback
        return {
            message: '',
            statusText: 'Unbekannt',
            color: '#A1A7AE'
        };
    };

    const handleBookingDetails = () => {
        navigate(`/bookings/details/${bookingId}`, { state: { userId: user } });
    };

    const handleRateBooking = () => {
        navigate(`/bookings/details/${bookingId}`, { state: { userId: user, openRatingModal: true } });
    };

    const canRateBooking = isCompletedBookingStatus(studio?.status) && studio?.guestReviewed !== true;

    const generateICSFile = () => {
        try {
            if (!studio.startDateTime || !studio.endDateTime) {
                console.error('Missing date or time information for calendar export');
                return;
            }
            const eventDateTime = new Date(studio.startDateTime);
            const endDateTime = new Date(studio.endDateTime);

            // Validate that dates are valid
            if (isNaN(eventDateTime.getTime()) || isNaN(endDateTime.getTime())) {
                console.error('Invalid date objects created');
                return;
            }

            // Format dates for ICS file
            const formatDateForICS = (date) => {
                return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
            };

            const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
DTSTART:${formatDateForICS(eventDateTime)}
DTEND:${formatDateForICS(endDateTime)}
SUMMARY:Termin bei ${studio.name || 'Studio'}
LOCATION:${studio.location || ''}
END:VEVENT
END:VCALENDAR`;

            const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.setAttribute('download', `termin_${(studio.name || 'studio').replace(/\s+/g, '_')}.ics`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error generating ICS file:', error);
        }
    };

    return (
        <div 
            className={classes.bookedCardContainer}
            style={hasNotification ? {
                border: '2.5px solid var(--stu-rot)',
                borderRadius: '20px',
                padding: '12px'
            } : {}}
        >
            <div className={classes.studioContainer}>
                <div className={classes.imageContainer}>
                    <img src={getStudioImageSrc(studio.image)} alt="Studio" />
                    {packageImage && (
                        <div className={classes.outsidePackageImageContainer}>
                            <div className={classes.packageImageContainer}>
                                <img src={getStudioImageSrc(packageImage)} alt="Package" />
                            </div>
                        </div>
                    )}
                </div>
                <div className={classes.studioInfoContainer}>
                    <h3 className={`semiboldLarge ${classes.truncatedTitle}`}>{studio.name}</h3>
                    <p className="regularSmall">{studio.location}</p>
                    {packageName && (
                        <>
                            <div className={classes.smallHorizontalLine}></div>
                            <p style={{color: 'var(--dark-grey)'}} className={`semiboldXs ${classes.truncatedPackageName}`}>{truncatedPackageName}</p>
                        </>
                    )}
                </div>
            </div>
            <div className={classes.horizontalLine}></div>
            <div className={classes.studioInfoContainer}>
                <h3 className="semiboldSmall">Datum und Zeit</h3>
                <p className="regularSmall">{studio.date}</p>
                <p className="regularSmall">{studio.time}</p>
            </div>
            <button onClick={generateICSFile} className={classes.calendarButton}>
                In Kalender exportieren
            </button>
            <div className={classes.horizontalLine}></div>
            <div className={classes.studioInfoContainer}>
                {(() => {
                    const statusDisplay = getStatusDisplay();
                    return (
                        <>
                            {statusDisplay.message && (
                                <p style={{marginBottom: '10px', color: 'var(--dark-grey-3)'}} className="regularLarge">
                                    {statusDisplay.message}
                                </p>
                            )}
                            <span className="semiboldMedium">Status</span>
                            <p style={{color: statusDisplay.color}} className="semiboldLarge">
                                {statusDisplay.statusText}
                            </p>
                        </>
                    );
                })()}
            </div>
            <div className={classes.buttonContainer}>
                <button onClick={handleBookingDetails} className={`${classes.button} mediumLarge`}>Buchungsdetails</button>
                {canRateBooking && (
                    <button onClick={handleRateBooking} className={`${classes.button} mediumLarge`} style={{ width: "100%", color: "white", backgroundColor: "var(--dark-grey)" }}>
                        Bewerten
                    </button>
                )}
            </div>
        </div>
    );
};

export default BookedCardPhone;
